import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const FixedExpensesGrid = ({ data, groups, onUpdate, onDelete, onCreateGroup, onDeleteGroup, searchQuery }) => {
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [showGroupManager, setShowGroupManager] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDesc, setNewGroupDesc] = useState('');
  const [creatingGroup, setCreatingGroup] = useState(false);

  const categoryOptions = [
    { value: 'office', label: 'Office Rent' },
    { value: 'utilities', label: 'Utilities' },
    { value: 'insurance', label: 'Insurance' },
    { value: 'admin', label: 'Administrative' },
    { value: 'other', label: 'Other' },
  ];

  const groupOptions = useMemo(() => [
    { value: '', label: '— No Group (Standalone) —' },
    ...(groups || [])?.map(g => ({ value: g?.name, label: g?.name })),
  ], [groups]);

  const filteredData = useMemo(() => {
    if (!searchQuery) return data;
    const query = searchQuery?.toLowerCase();
    return data?.filter(item =>
      item?.name?.toLowerCase()?.includes(query) ||
      item?.category?.toLowerCase()?.includes(query) ||
      item?.groupName?.toLowerCase()?.includes(query)
    );
  }, [data, searchQuery]);

  // Separate standalone items from grouped items
  const { standaloneItems, groupedItems, orderedGroupNames } = useMemo(() => {
    const standalone = filteredData?.filter(item => !item?.groupName);
    const grouped = filteredData?.filter(item => !!item?.groupName);

    const groupMap = {};
    grouped?.forEach(item => {
      const g = item?.groupName;
      if (!groupMap?.[g]) groupMap[g] = [];
      groupMap?.[g]?.push(item);
    });

    // Order groups by the groups list order, then any orphan groups alphabetically
    const definedGroupNames = (groups || [])?.map(g => g?.name);
    const allGroupNamesInData = Object.keys(groupMap);
    const ordered = [
      ...definedGroupNames?.filter(n => groupMap?.[n]),
      ...allGroupNamesInData?.filter(n => !definedGroupNames?.includes(n))?.sort(),
    ];

    return { standaloneItems: standalone, groupedItems: groupMap, orderedGroupNames: ordered };
  }, [filteredData, groups]);

  const handleEdit = (item) => {
    setEditingId(item?.id);
    setEditData({ ...item });
  };

  const handleSave = () => {
    onUpdate(editData);
    setEditingId(null);
    setEditData({});
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditData({});
  };

  const handleSelectRow = (id) => {
    setSelectedRows(prev =>
      prev?.includes(id) ? prev?.filter(rowId => rowId !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedRows?.length === filteredData?.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(filteredData?.map(item => item?.id));
    }
  };

  const toggleGroup = (groupName) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupName]: prev?.[groupName] === false ? true : false,
    }));
  };

  const isGroupExpanded = (groupName) => expandedGroups?.[groupName] !== false;

  const formatCurrency = (value) => `OMR ${parseFloat(value || 0)?.toFixed(3)}`;

  const handleCreateGroup = async () => {
    if (!newGroupName?.trim()) return;
    setCreatingGroup(true);
    try {
      await onCreateGroup?.({ name: newGroupName?.trim(), description: newGroupDesc?.trim() });
      setNewGroupName('');
      setNewGroupDesc('');
    } finally {
      setCreatingGroup(false);
    }
  };

  const renderEditRow = (item) => (
    <tr key={`edit-${item?.id}`} className="border-b border-border bg-primary/5">
      <td className="px-4 py-3">
        <input
          type="checkbox"
          checked={selectedRows?.includes(item?.id)}
          onChange={() => handleSelectRow(item?.id)}
          className="w-4 h-4 rounded border-border"
        />
      </td>
      <td className="px-4 py-3">
        <Input
          type="text"
          value={editData?.name || ''}
          onChange={(e) => setEditData({ ...editData, name: e?.target?.value })}
          className="w-full"
        />
      </td>
      <td className="px-4 py-3">
        <Select
          options={categoryOptions}
          value={editData?.category}
          onChange={(value) => setEditData({ ...editData, category: value })}
        />
      </td>
      <td className="px-4 py-3">
        <Input
          type="number"
          value={editData?.monthlyCost || ''}
          onChange={(e) => setEditData({ ...editData, monthlyCost: parseFloat(e?.target?.value) })}
          className="w-full text-right"
        />
      </td>
      <td className="px-4 py-3">
        <Input
          type="text"
          value={editData?.description || ''}
          onChange={(e) => setEditData({ ...editData, description: e?.target?.value })}
          className="w-full"
        />
      </td>
      <td className="px-4 py-3">
        <Select
          options={groupOptions}
          value={editData?.groupName || ''}
          onChange={(value) => setEditData({ ...editData, groupName: value || null })}
        />
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-center gap-1">
          <Button variant="success" size="xs" iconName="Check" onClick={handleSave} />
          <Button variant="ghost" size="xs" iconName="X" onClick={handleCancel} />
        </div>
      </td>
    </tr>
  );

  const renderStandaloneRow = (item) => (
    <tr key={item?.id} className="border-b border-border hover:bg-muted/50 transition-smooth">
      <td className="px-4 py-3">
        <input
          type="checkbox"
          checked={selectedRows?.includes(item?.id)}
          onChange={() => handleSelectRow(item?.id)}
          className="w-4 h-4 rounded border-border"
        />
      </td>
      <td className="px-4 py-3">
        <span className="text-sm text-foreground font-medium">{item?.name}</span>
      </td>
      <td className="px-4 py-3">
        <span className="text-sm text-muted-foreground capitalize">{item?.category}</span>
      </td>
      <td className="px-4 py-3 text-right">
        <span className="text-sm font-medium text-foreground">{formatCurrency(item?.monthlyCost)}</span>
      </td>
      <td className="px-4 py-3">
        <span className="text-sm text-muted-foreground">{item?.description}</span>
      </td>
      <td className="px-4 py-3">
        <span className="text-xs text-muted-foreground italic">Standalone</span>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-center gap-1">
          <Button variant="ghost" size="xs" iconName="Edit" onClick={() => handleEdit(item)} />
          <Button variant="ghost" size="xs" iconName="Trash2" onClick={() => onDelete(item?.id)} />
        </div>
      </td>
    </tr>
  );

  const renderGroupedItem = (item) => (
    <tr key={item?.id} className="border-b border-border bg-muted/20 hover:bg-muted/40 transition-smooth">
      <td className="px-4 py-3">
        <input
          type="checkbox"
          checked={selectedRows?.includes(item?.id)}
          onChange={() => handleSelectRow(item?.id)}
          className="w-4 h-4 rounded border-border"
        />
      </td>
      <td className="px-4 py-3 pl-10">
        <div className="flex items-center gap-2">
          <Icon name="CornerDownRight" size={13} className="text-muted-foreground flex-shrink-0" />
          <span className="text-sm text-foreground">{item?.name}</span>
        </div>
      </td>
      <td className="px-4 py-3">
        <span className="text-sm text-muted-foreground capitalize">{item?.category}</span>
      </td>
      <td className="px-4 py-3 text-right">
        <span className="text-sm font-medium text-foreground">{formatCurrency(item?.monthlyCost)}</span>
      </td>
      <td className="px-4 py-3">
        <span className="text-sm text-muted-foreground">{item?.description}</span>
      </td>
      <td className="px-4 py-3">
        <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">{item?.groupName}</span>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-center gap-1">
          <Button variant="ghost" size="xs" iconName="Edit" onClick={() => handleEdit(item)} />
          <Button variant="ghost" size="xs" iconName="Trash2" onClick={() => onDelete(item?.id)} />
        </div>
      </td>
    </tr>
  );

  return (
    <div className="space-y-4">
      {/* Group Manager Panel */}
      <div className="border border-border rounded-lg overflow-hidden">
        <button
          onClick={() => setShowGroupManager(prev => !prev)}
          className="w-full flex items-center justify-between px-4 py-3 bg-muted/50 hover:bg-muted transition-colors text-left"
        >
          <div className="flex items-center gap-2">
            <Icon name="FolderOpen" size={16} className="text-primary" />
            <span className="text-sm font-semibold text-foreground">Expense Groups</span>
            <span className="text-xs bg-primary/15 text-primary px-2 py-0.5 rounded-full font-medium">
              {groups?.length || 0} groups
            </span>
          </div>
          <Icon name={showGroupManager ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-muted-foreground" />
        </button>

        {showGroupManager && (
          <div className="p-4 border-t border-border bg-background">
            {/* Create new group */}
            <div className="flex items-end gap-3 mb-4">
              <div className="flex-1">
                <label className="block text-xs font-medium text-muted-foreground mb-1">Group Name</label>
                <Input
                  type="text"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e?.target?.value)}
                  placeholder="e.g. Salaries, Software Subscriptions"
                  className="w-full"
                  onKeyDown={(e) => e?.key === 'Enter' && handleCreateGroup()}
                />
              </div>
              <div className="flex-1">
                <label className="block text-xs font-medium text-muted-foreground mb-1">Description (optional)</label>
                <Input
                  type="text"
                  value={newGroupDesc}
                  onChange={(e) => setNewGroupDesc(e?.target?.value)}
                  placeholder="Brief description"
                  className="w-full"
                />
              </div>
              <Button
                variant="primary"
                size="sm"
                iconName="Plus"
                onClick={handleCreateGroup}
                disabled={!newGroupName?.trim() || creatingGroup}
              >
                {creatingGroup ? 'Creating...' : 'Add Group'}
              </Button>
            </div>

            {/* Existing groups list */}
            {groups?.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {groups?.map(group => {
                  const itemCount = groupedItems?.[group?.name]?.length || 0;
                  const groupTotal = groupedItems?.[group?.name]?.reduce((sum, i) => sum + (parseFloat(i?.monthlyCost) || 0), 0) || 0;
                  return (
                    <div key={group?.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border border-border">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <Icon name="Folder" size={14} className="text-primary flex-shrink-0" />
                          <span className="text-sm font-medium text-foreground truncate">{group?.name}</span>
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {itemCount} {itemCount === 1 ? 'item' : 'items'} · {formatCurrency(groupTotal)}
                        </div>
                      </div>
                      <button
                        onClick={() => onDeleteGroup?.(group?.id)}
                        className="ml-2 p-1 rounded hover:bg-destructive/10 hover:text-destructive text-muted-foreground transition-colors flex-shrink-0"
                        title="Delete group"
                      >
                        <Icon name="Trash2" size={13} />
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-3">
                No groups yet. Create your first group above.
              </p>
            )}
          </div>
        )}
      </div>
      {/* Data Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse min-w-[900px]">
          <thead>
            <tr className="bg-muted border-b border-border">
              <th className="w-12 px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedRows?.length === filteredData?.length && filteredData?.length > 0}
                  onChange={handleSelectAll}
                  className="w-4 h-4 rounded border-border"
                />
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Expense Name</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Category</th>
              <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">Monthly Cost</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Description</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Group</th>
              <th className="w-24 px-4 py-3 text-center text-sm font-semibold text-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {/* Grouped expense sections */}
            {orderedGroupNames?.map((groupName) => {
              const items = groupedItems?.[groupName];
              if (!items?.length) return null;
              const expanded = isGroupExpanded(groupName);
              const groupTotal = items?.reduce((sum, i) => sum + (parseFloat(i?.monthlyCost) || 0), 0);

              return (
                <React.Fragment key={`group-${groupName}`}>
                  {/* Group header row */}
                  <tr
                    className="border-b border-border bg-primary/5 hover:bg-primary/10 transition-smooth cursor-pointer select-none"
                    onClick={() => toggleGroup(groupName)}
                  >
                    <td className="px-4 py-3">
                      <div className="w-4 h-4" />
                    </td>
                    <td className="px-4 py-3" colSpan={1}>
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 flex items-center justify-center rounded hover:bg-primary/20 transition-colors">
                          <Icon name={expanded ? 'ChevronDown' : 'ChevronRight'} size={14} className="text-primary" />
                        </div>
                        <Icon name={expanded ? 'FolderOpen' : 'Folder'} size={15} className="text-primary" />
                        <span className="text-sm font-semibold text-foreground">{groupName}</span>
                        <span className="text-xs bg-primary/15 text-primary px-2 py-0.5 rounded-full font-medium">
                          {items?.length} {items?.length === 1 ? 'item' : 'items'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-muted-foreground italic">Group</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-sm font-semibold text-primary">{formatCurrency(groupTotal)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-muted-foreground italic">
                        {expanded ? 'Click to collapse' : 'Click to expand'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">{groupName}</span>
                    </td>
                    <td className="px-4 py-3" />
                  </tr>
                  {/* Group items */}
                  {expanded && items?.map((item) =>
                    editingId === item?.id ? renderEditRow(item) : renderGroupedItem(item)
                  )}
                  {/* Group subtotal row */}
                  {expanded && items?.length > 1 && (
                    <tr className="border-b-2 border-primary/20 bg-muted/30">
                      <td colSpan={3} className="px-4 py-2 pl-10">
                        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                          {groupName} — Total ({items?.length} items)
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right">
                        <span className="text-sm font-bold text-primary">{formatCurrency(groupTotal)}</span>
                      </td>
                      <td colSpan={3} />
                    </tr>
                  )}
                </React.Fragment>
              );
            })}

            {/* Standalone items */}
            {standaloneItems?.length > 0 && orderedGroupNames?.length > 0 && (
              <tr className="border-b border-border bg-muted/30">
                <td colSpan={7} className="px-4 py-2">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Standalone Expenses
                  </span>
                </td>
              </tr>
            )}
            {standaloneItems?.map((item) =>
              editingId === item?.id ? renderEditRow(item) : renderStandaloneRow(item)
            )}
          </tbody>
        </table>

        {filteredData?.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Icon name="FileX" size={48} className="text-muted-foreground mb-4" />
            <p className="text-sm text-muted-foreground">No fixed expenses found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FixedExpensesGrid;